// src/features/spotting/pages/AddSighting.jsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../../firebase';
import { 
  collection, addDoc, serverTimestamp, query, where, getDocs
} from 'firebase/firestore';
import { Camera, Loader2, MapPin, Image as ImageIcon, Youtube, X, FileText, Sparkles } from 'lucide-react';
import axios from 'axios';
import { getEmojiFlag } from '../../../utils/helpers';
import { applyWatermark, uploadImage } from '../../../utils/imageUtils';
import heic2any from "heic2any";
import { DuplicateModal } from '../../../components/modals/DuplicateModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export const AddSighting = ({ showToast }) => {
  const [files, setFiles] = useState([]);
  const [plate, setPlate] = useState('');
  const [brand, setBrand] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [showManualLocation, setShowManualLocation] = useState(false);
  const [locData, setLocData] = useState({ 
    name: "Pobierz automatycznie", 
    emoji: "🌍", 
    code: "PL", 
    lat: null, 
    lng: null 
  });
  const [duplicateData, setDuplicateData] = useState(null);
  
  // ========================================
  // NOWE - PORADNIK MODE
  // ========================================
  const [isPoradnik, setIsPoradnik] = useState(false);
  const [seoSlug, setSeoSlug] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');
  
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();

  // Helper: auto-generate SEO slug from title
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
      .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
      .replace(/ś/g, 's').replace(/ź|ż/g, 'z')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Auto-slug when seoTitle changes
  const handleSeoTitleChange = (value) => {
    setSeoTitle(value);
    if (!seoSlug || seoSlug === generateSlug(seoTitle)) {
      setSeoSlug(generateSlug(value));
    }
  };

  // Przetwarzanie pojedynczego zdjęcia (HEIC + resize + watermark)
  const processImage = async (originalFile) => {
    let currentBlob = originalFile;

    // HEIC → JPEG
    if (
      originalFile.name?.toLowerCase().endsWith(".heic") || 
      originalFile.type === "image/heic"
    ) {
      setStatus('Konwertowanie HEIC...');
      const converted = await heic2any({ blob: originalFile, toType: "image/jpeg", quality: 0.3 });
      currentBlob = Array.isArray(converted) ? converted[0] : converted;
    }

    // Resize do 800px
    setStatus('Skalowanie...');
    const bitmap = await createImageBitmap(currentBlob, { resizeWidth: 800 });
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    return new Promise((resolve) => {
      canvas.toBlob(async (blob) => {
        // Watermark
        try {
          const watermarked = await applyWatermark(blob);
          resolve(watermarked);
        } catch {
          resolve(blob);
        }
      }, 'image/jpeg', 0.7);
    });
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 3) {
      showToast("Maksymalnie 3 zdjęcia", "error");
      return;
    }
    setFiles(prev => [...prev, ...selectedFiles].slice(0, 3));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleLocate = () => {
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await axios.get(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`
          );
          const code = res.data.address.country_code.toUpperCase();
          const cityName = 
            res.data.address.city || 
            res.data.address.town || 
            res.data.address.village || 
            res.data.address.suburb ||
            "Nieznane miasto";
          
          setLocData({
            name: `${cityName}, ${code}`,
            emoji: getEmojiFlag(code),
            code,
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
        } catch {
          showToast("Błąd geolokalizacji", "error");
        } finally {
          setLocLoading(false);
        }
      },
      () => {
        setLocLoading(false);
        setShowManualLocation(true);
      }
    );
  };

  const handleManualLocation = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const cityName = fd.get('city');
    const countryName = fd.get('country');
    const countryCode = {
      'Polska': 'PL',
      'Niemcy': 'DE',
      'Czechy': 'CZ',
      'Słowacja': 'SK',
      'Austria': 'AT'
    }[countryName] || 'PL';
    
    setLocData({
      name: `${cityName}, ${countryName}`,
      emoji: getEmojiFlag(countryCode),
      code: countryCode,
      lat: null,
      lng: null
    });
    setShowManualLocation(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) return showToast("Zaloguj się", "error");
    
    // ========================================
    // VALIDATION - PORADNIK
    // ========================================
    if (isPoradnik) {
      if (!seoTitle.trim()) return showToast("Podaj tytuł artykułu", "error");
      if (!seoSlug.trim()) return showToast("Podaj SEO slug", "error");
      if (description.length < 50) return showToast("Opis artykułu musi mieć min. 50 znaków", "error");
      
      // Check if slug is unique
      try {
        const slugQuery = query(
          collection(db, 'sightings'),
          where('seoSlug', '==', seoSlug.toLowerCase().trim())
        );
        const slugDocs = await getDocs(slugQuery);
        if (!slugDocs.empty) {
          return showToast("Ten slug już istnieje! Zmień go.", "error");
        }
      } catch (error) {
        console.error('Error checking slug:', error);
      }
    } else {
      // ========================================
      // VALIDATION - CHAMSTWO (normal mode)
      // ========================================
      if (!plate.trim()) return showToast("Podaj tablicę", "error");
      if (!brand.trim()) return showToast("Podaj markę", "error");
      if (description.length < 20) return showToast("Opis musi mieć min. 20 znaków", "error");
      
      // ========================================
      // CHECK CZY TABLICA JUŻ ISTNIEJE
      // ========================================
      const plateNormalized = plate.toUpperCase().replace(/\s/g, '');
      
      try {
        const existingQuery = query(
          collection(db, 'sightings'),
          where('plate', '==', plateNormalized),
          where('status', '==', 'verified')
        );
        
        const existingDocs = await getDocs(existingQuery);
        
        if (!existingDocs.empty) {
          // TABLICA ISTNIEJE - pokaż modal
          const existingDoc = existingDocs.docs[0];
          setDuplicateData({
            id: existingDoc.id,
            plate: plateNormalized,
            ...existingDoc.data()
          });
          return; // STOP - nie uploaduj
        }
      } catch (error) {
        console.error('Error checking duplicates:', error);
        // Jeśli check się nie powiedzie - kontynuuj normalnie
      }
    }
    
    // ========================================
    // UPLOAD IMAGES
    // ========================================
    
    setUploading(true);
    try {
      // Przetwarzanie i upload wszystkich zdjęć z thumbnails
      const imageUrls = [];
      const thumbUrls = [];
      
      if (files.length > 0) {
        // Jeśli są zdjęcia - przetwarzaj normalnie
        for (let i = 0; i < files.length; i++) {
          setStatus(`Przetwarzanie zdjęcia ${i + 1}/${files.length}...`);
          const processedBlob = await processImage(files[i]);
          
          setStatus(`Wysyłanie zdjęcia ${i + 1}/${files.length}...`);
          const { full, thumb } = await uploadImage(processedBlob, setStatus);
          imageUrls.push(full);
          thumbUrls.push(thumb);
        }
      } else if (!isPoradnik) {
        // Brak zdjęć w trybie chamstwo - użyj placeholdera
        const placeholderUrl = '/no-photo-placeholder.svg';
        imageUrls.push(placeholderUrl);
        thumbUrls.push(placeholderUrl);
      }
      
      // Browser fingerprint
      const fingerprint = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timestamp: new Date().toISOString()
      };

      // ========================================
      // BUILD PAYLOAD
      // ========================================
      
      const baseData = {
        userId: auth.currentUser.uid, 
        createdAt: serverTimestamp(), 
        locationName: locData.name,
        countryEmoji: locData.emoji, 
        countryCode: locData.code, 
        lat: locData.lat, 
        lng: locData.lng,
        videoUrl: videoUrl || null,
        upvotes: 0, 
        commentCount: 0, 
        status: 'pending',
        fingerprint
      };

      let sightingData;

      if (isPoradnik) {
        // ========================================
        // PORADNIK DATA
        // ========================================
        sightingData = {
          ...baseData,
          flair: 'poradnik',
          brand: 'Poradnik',
          description,
          plate: null,
          plateSlug: null, // ✅WAŻNE: null dla poradników!
          
          // SEO fields
          seoSlug: seoSlug.toLowerCase().trim(),
          seoTitle: seoTitle.trim(),
          seoDescription: seoDescription.trim() || description.substring(0, 155).replace(/<[^>]*>/g, ''), // Strip HTML tags
          seoKeywords: seoKeywords.split(',').map(k => k.trim()).filter(Boolean),
          
          // Images (optional dla poradników)
          imageUrl: imageUrls[0] || null,
          thumbUrl: thumbUrls[0] || null,
          images: imageUrls,
          thumbs: thumbUrls
        };
      } else {
        // ========================================
        // CHAMSTWO DATA (normal)
        // ========================================
        const plateNorm = plate.toUpperCase().replace(/\s/g, '');
        
        sightingData = {
          ...baseData,
          flair: 'chamstwo',
          plate: plateNorm,
          plateSlug: plateNorm.toLowerCase().replace(/[^a-z0-9]/g, ''),
          brand,
          description,
          imageUrl: imageUrls[0],
          thumbUrl: thumbUrls[0],
          images: imageUrls,
          thumbs: thumbUrls
        };
      }

      await addDoc(collection(db, "sightings"), sightingData);
      
      if (isPoradnik) {
        showToast("Artykuł dodany! Czeka na weryfikację 📰");
      } else {
        showToast("Dodano! 🎉");
      }
      
      navigate('/');
    } catch (err) { 
      console.error("Błąd dodawania:", err);
      showToast(err.message || "Błąd wysyłania", "error"); 
    } finally { 
      setUploading(false); 
      setStatus(''); 
    }
  };

  // React-Quill modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link'
  ];

  return (
    <div className="max-w-lg mx-auto p-4 pb-32">
      {/* Duplicate Plate Modal */}
      {duplicateData && (
        <DuplicateModal
          plate={duplicateData.plate}
          existingPost={duplicateData}
          onClose={() => setDuplicateData(null)}
        />
      )}
      
      {/* Manual Location Modal */}
      {showManualLocation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[700] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4">Dodaj lokalizację ręcznie</h3>
            <p className="text-sm text-gray-600 mb-4">GPS niedostępny - wpisz dane ręcznie</p>
            
            <form onSubmit={handleManualLocation} className="space-y-3">
              <input
                name="city"
                type="text"
                placeholder="Miasto *"
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                name="country"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Polska">🇵🇱 Polska</option>
                <option value="Niemcy">🇩🇪 Niemcy</option>
                <option value="Czechy">🇨🇿 Czechy</option>
                <option value="Słowacja">🇸🇰 Słowacja</option>
                <option value="Austria">🇦🇹 Austria</option>
              </select>
              
              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowManualLocation(false)}
                  className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-semibold transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all"
                >
                  Zatwierdź
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold">
          {isPoradnik ? 'Napisz artykuł' : 'Zgłoś obserwację'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isPoradnik ? 'Utwórz poradnik dla społeczności' : 'Zdjęcia opcjonalne (0-3)'}
        </p>
      </div>

      {/* ========================================
          TOGGLE: CHAMSTWO vs PORADNIK
          ======================================== */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
        <label className="flex items-center gap-3 cursor-pointer">
          <input 
            type="checkbox" 
            checked={isPoradnik}
            onChange={(e) => setIsPoradnik(e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-blue-600" />
            <span className="font-bold text-gray-900">
              To jest poradnik/artykuł
            </span>
          </div>
        </label>
        {isPoradnik && (
          <p className="text-xs text-blue-600 mt-2 ml-8">
            Tryb artykułu: Tablica i marka niewymagane. Dodaj SEO meta poniżej.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Multiple Photos Grid */}
        <div className="space-y-3">
          {files.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {files.map((file, index) => (
                <div key={index} className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden">
                  <img 
                    src={URL.createObjectURL(file)} 
                    className="w-full h-full object-cover" 
                    alt={`Zdjęcie ${index + 1}`} 
                  />
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full shadow"
                  >
                    <X size={14}/>
                  </button>
                  {index === 0 && (
                    <div className="absolute bottom-1 left-1 bg-blue-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">
                      Główne
                    </div>
                  )}
                </div>
              ))}
              
              {files.length < 3 && (
                <button
                  type="button"
                  onClick={() => galleryInputRef.current.click()}
                  className="aspect-square bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-all"
                >
                  <ImageIcon size={24}/>
                </button>
              )}
            </div>
          ) : (
            <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-2xl aspect-video flex items-center justify-center">
              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={() => cameraInputRef.current.click()} 
                  className="bg-blue-600 text-white p-4 rounded-xl hover:bg-blue-700 transition-colors"
                >
                  <Camera size={24}/>
                </button>
                <button 
                  type="button" 
                  onClick={() => galleryInputRef.current.click()} 
                  className="bg-gray-200 text-gray-600 p-4 rounded-xl hover:bg-gray-300 transition-colors"
                >
                  <ImageIcon size={24}/>
                </button>
              </div>
            </div>
          )}
        </div>

        <input 
          type="file" 
          ref={cameraInputRef} 
          capture="environment" 
          accept="image/*"
          onChange={handleFileSelect} 
          className="hidden" 
        />
        <input 
          type="file" 
          ref={galleryInputRef} 
          accept="image/*"
          multiple
          onChange={handleFileSelect} 
          className="hidden" 
        />

        {/* ========================================
            POLA SEO - TYLKO DLA PORADNIKÓW
            ======================================== */}
        {isPoradnik && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 text-blue-700 font-bold text-sm mb-2">
              <FileText size={18} />
              <span>Pola SEO (artykuł)</span>
            </div>

            {/* SEO Title */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Tytuł artykułu (SEO) *
              </label>
              <input 
                type="text" 
                placeholder="np: Gdzie zgłosić złe parkowanie? Poradnik 2026" 
                className="w-full p-3 rounded-xl bg-white border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500" 
                value={seoTitle} 
                onChange={e => handleSeoTitleChange(e.target.value)} 
                required={isPoradnik}
              />
              <p className="text-xs text-gray-500 mt-1">
                {seoTitle.length}/70 znaków (idealnie 50-60)
              </p>
            </div>

            {/* SEO Slug */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                SEO Slug (URL) *
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">motoradar24.pl/poradnik/</span>
                <input 
                  type="text" 
                  placeholder="gdzie-zglosic-zle-parkowanie" 
                  className="flex-1 p-3 rounded-xl bg-white border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm" 
                  value={seoSlug} 
                  onChange={e => setSeoSlug(generateSlug(e.target.value))} 
                  required={isPoradnik}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Tylko małe litery, cyfry i myślniki
              </p>
            </div>

            {/* SEO Description */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Meta Description (opcjonalne)
              </label>
              <textarea 
                placeholder="Krótki opis dla Google (150-160 znaków). Jeśli puste, użyjemy początku artykułu." 
                className="w-full p-3 h-20 bg-white rounded-xl border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500 resize-none" 
                value={seoDescription} 
                onChange={e => setSeoDescription(e.target.value)} 
              />
              <p className="text-xs text-gray-500 mt-1">
                {seoDescription.length}/160 znaków
              </p>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Keywords (opcjonalne)
              </label>
              <input 
                type="text" 
                placeholder="zgłoszenie, parkowanie, straż miejska, 986" 
                className="w-full p-3 rounded-xl bg-white border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500" 
                value={seoKeywords} 
                onChange={e => setSeoKeywords(e.target.value)} 
              />
              <p className="text-xs text-gray-500 mt-1">
                Oddzielone przecinkami
              </p>
            </div>
          </div>
        )}

        {/* Location */}
        <div className="space-y-2">
          <button 
            type="button" 
            onClick={handleLocate} 
            disabled={locLoading}
            className="w-full p-4 bg-gray-50 rounded-xl border flex justify-between items-center hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <span className="flex items-center gap-2">
              <span>{locData.emoji}</span>
              <span className="font-semibold">{locData.name}</span>
            </span>
            {locLoading 
              ? <Loader2 size={20} className="text-blue-600 animate-spin" />
              : <MapPin size={20} className="text-blue-600" />
            }
          </button>
          
          <button
            type="button"
            onClick={() => setShowManualLocation(true)}
            className="w-full text-xs text-blue-600 hover:text-blue-700 font-semibold py-1"
          >
            Lub wpisz ręcznie →
          </button>
        </div>

        {/* Plate - HIDE gdy poradnik */}
        {!isPoradnik && (
          <input 
            type="text" 
            placeholder="TABLICA (MAX 9 ZNAKÓW)" 
            maxLength={9}
            className="w-full p-4 rounded-xl bg-gray-50 font-bold text-center uppercase outline-none focus:ring-2 focus:ring-blue-600 border border-gray-200" 
            value={plate} 
            onChange={e => setPlate(e.target.value.toUpperCase().replace(/\s/g, ''))} 
            required
          />
        )}

        {/* Brand - HIDE gdy poradnik */}
        {!isPoradnik && (
          <input 
            type="text" 
            placeholder="Marka auta (np. BMW, Audi) *" 
            className="w-full p-4 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-600 border border-gray-200" 
            value={brand} 
            onChange={e => setBrand(e.target.value)} 
            required
          />
        )}
        
        {/* Description - RICH TEXT EDITOR dla poradników */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            {isPoradnik ? 'Treść artykułu *' : 'Opis sytuacji *'}
          </label>
          
          {isPoradnik ? (
            <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
              <ReactQuill
                theme="snow"
                value={description}
                onChange={setDescription}
                placeholder="Napisz artykuł... Użyj paska narzędzi do formatowania."
                modules={quillModules}
                formats={quillFormats}
                style={{ minHeight: '300px' }}
              />
            </div>
          ) : (
            <textarea 
              placeholder="Opis sytuacji (min 20 znaków) *"
              className="w-full p-4 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-blue-600 resize-none border border-gray-200" 
              rows={5}
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              required
            />
          )}
          
          {!isPoradnik && (
            <p className="text-xs text-gray-500 mt-1">
              {description.length} / 20 min
            </p>
          )}
        </div>

        {/* Video */}
        <div className="space-y-2">
          <button 
            type="button"
            onClick={() => setShowVideoInput(!showVideoInput)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-semibold"
          >
            <Youtube size={18}/>
            {showVideoInput ? 'Usuń link do filmu' : 'Dodaj link do filmu (opcjonalne)'}
          </button>
          
          {showVideoInput && (
            <input 
              type="url" 
              placeholder="https://youtube.com/watch?v=..." 
              className="w-full p-4 rounded-xl bg-red-50 border border-red-200 outline-none focus:ring-2 focus:ring-red-500" 
              value={videoUrl} 
              onChange={e => setVideoUrl(e.target.value)} 
            />
          )}
        </div>

        {/* Submit Button */}
        <button 
          type="submit" 
          disabled={uploading}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-5 rounded-xl font-black text-sm tracking-widest uppercase shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={20} className="animate-spin"/>
              {status || 'Wysyłanie...'}
            </span>
          ) : (
            isPoradnik ? 'Publikuj artykuł' : 'Dodaj obserwację'
          )}
        </button>
      </form>
    </div>
  );
};